/**
 * Переходы режимов «Скорость» и «Точность».
 *
 * Режимы собирают занятие сами, но mastery и XP считаются теми же функциями,
 * что и после обычной тренировки: результат для пользователя один и тот же, а
 * два разных пути его подсчёта разошлись бы (docs/ARCHITECTURE.md §5).
 */

import type { SessionId } from '@type2x/shared-contracts';
import type { TypingSession } from '@type2x/typing-engine';
import { DEFAULT_LEARNING_CONFIG, applyUnitOutcomes } from '@type2x/learning-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import {
  activeProfile,
  appendXp,
  recordSession,
  setTempoTarget,
  upsertMastery,
} from '@type2x/feature-personal-workspace';
import {
  achievedTargetCpm,
  advanceTempo,
  collectTempoOutcomes,
  currentDrill,
  startTempoSession,
  tempoUnits,
  type TempoMode,
  type TempoSession,
  type TempoSnapshot,
} from '@type2x/feature-tempo';
import { buildSessionSummary } from '@type2x/feature-progress';
import { buildSessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from './app-state.js';
import { countXpToday, resultScreen, type Transition } from './flow.js';

const CONFIG = DEFAULT_LEARNING_CONFIG;

/** Снимок навыка для режимов темпа. */
export function tempoSnapshot(workspace: Workspace): TempoSnapshot {
  const profile = activeProfile(workspace);

  return {
    language: profile.language,
    layoutId: profile.keyboard.layoutId,
    units: profile.units,
    stats: profile.stats,
    baselineCpm: profile.baseline?.cpm ?? 0,
    targetCpm: profile.tempoTargetCpm,
  };
}

/** Экран режима со свежим занятием. */
export function tempoScreen(runtime: Runtime, workspace: Workspace, mode: TempoMode): AppScreen {
  return {
    name: 'tempo',
    session: startTempoSession(
      tempoSnapshot(workspace),
      mode,
      `${runtime.installationId}:${mode}:${runtime.clock.epochMs()}`,
    ),
    sessionId: runtime.ids.newSessionId(),
  };
}

/** Переводит занятие вперёд по итогам прогона. */
export function advanceRun(session: TempoSession, run: TypingSession): TempoSession {
  if (currentDrill(session) === null) return session;

  return advanceTempo(session, {
    metrics: run.metrics,
    events: run.keystrokeEvents,
    errors: run.typingErrors,
  });
}

/**
 * Завершает занятие: обновляет слова, запоминает ступень и собирает итог.
 *
 * Ступень темпа сохраняется в профиле: она набрана за несколько занятий, и
 * начинать её заново с каждым входом значило бы их обесценить.
 */
export function finishTempo(
  runtime: Runtime,
  workspace: Workspace,
  session: TempoSession,
  sessionId: SessionId,
): Transition {
  const now = runtime.clock.isoNow();
  const profile = activeProfile(workspace);

  const units = tempoUnits(session);
  const outcomes = collectTempoOutcomes(session);
  const statsAfter = applyUnitOutcomes(units, profile.stats, outcomes, CONFIG, now);

  // Слово могло встретиться впервые: его единица создаётся вместе с итогом.
  let next = upsertMastery(workspace, units, statsAfter, now);
  next = recordSession(next, session.metrics, now);
  next = setTempoTarget(next, achievedTargetCpm(session), now);

  const summary = buildSessionSummary({
    metrics: session.metrics,
    baseline: profile.baseline,
    units: activeProfile(next).units,
    statsBefore: profile.stats,
    statsAfter,
    previousXp: next.xpLedger.reduce((sum, item) => sum + item.amount, 0),
    xpOccurrencesToday: countXpToday(next, now),
    activeDays: activeProfile(next).practiceDays.length,
    retained: false,
    isControlled: false,
    awardedAt: now,
    config: CONFIG,
  });

  for (const transaction of summary.xpAwarded) {
    next = appendXp(next, transaction, now);
  }

  return {
    workspace: next,
    screen: resultScreen(next, summary, { kind: 'tempo', mode: session.mode }, now),
    sessionPayload: buildSessionPayload({
      syncId: runtime.ids.newSyncId(),
      sessionId,
      mode: session.mode,
      language: profile.language,
      layoutId: profile.keyboard.layoutId,
      startedAt: new Date(runtime.clock.epochMs() - session.metrics.elapsedMs).toISOString(),
      durationMs: session.metrics.elapsedMs,
      metrics: session.metrics,
      outcomes,
      statsAfter,
      errors: session.errors,
      isControlled: false,
    }),
  };
}
